/**
 * The four firm-economics widgets (US #2239, task #2278).
 *
 * Containers are `GlassCard`; the three lists are the shared `Table` (compact,
 * sized to their rows) so the panes read like every other table in the app. Chart colours come from `useChartTokens`, the same brand tokens the
 * matter economics charts read, so they flip with the theme instead of sitting
 * on ink as a fixed indigo.
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  PiBriefcaseLight,
  PiBuildingsLight,
  PiChartBarLight,
  PiUsersLight,
} from 'react-icons/pi';
import { formatCurrency } from '../../utils/formatCurrency';
import GlassCard from '../shared/brand/GlassCard';
import Table from '../shared/TailwindComponents/Table';
import Badge from '../shared/TailwindComponents/Badge';
import UserCell from '../Matters/economics/UserCell';
import { useChartTokens } from '../Matters/economics/chartTokens';
import type {
  FirmTopMatter,
  FirmTopClient,
  FirmUserProductivity,
} from '../../types/economics';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const money = (currency: string, v?: number | null) => formatCurrency(Number(v || 0), currency);

const EmptyRow: React.FC<{ label: string }> = ({ label }) => (
  <p className="text-sm text-fg-muted py-4 text-center">{label}</p>
);

/**
 * The pane table: the shared `Table`, compact, sized to its rows (no fixed
 * 100vh box inside a card) and not resizable - these are short top-N lists
 * where a drag handle per column is noise.
 */
const PaneTable: React.FC<{
  tableId: string;
  columns: string[];
  data: Record<string, any>[];
  onRowClick?: (row: any) => void;
  renderCell?: (column: string, value: any, row: any) => React.ReactNode;
}> = ({ tableId, columns, data, onRowClick, renderCell }) => (
  <Table
    tableId={tableId}
    columns={columns}
    data={data}
    onRowClick={onRowClick}
    renderCell={renderCell}
    rowSize="compact"
    resizableColumns={false}
    min="min-h-0"
    max="max-h-80"
  />
);

const hours = (v: number | null | undefined, unit: string) => `${Number(v || 0).toFixed(1)} ${unit}`;

/** Utilisation as a toned badge: the one figure in these tables worth a glance (R4-13). */
export const UtilizationBadge: React.FC<{ pct: number | null }> = ({ pct }) => {
  if (pct == null) return <span className="text-fg-muted">—</span>;
  const color = pct >= 75 ? 'green' : pct >= 50 ? 'yellow' : 'gray';
  return <Badge color={color}>{`${pct}%`}</Badge>;
};

const toPct = (ratio?: number | null) => (ratio != null ? Math.round(ratio * 100) : null);

/** Top matters by revenue. Clicking a row drills into its Economics tab (#2280). */
export const TopMattersWidget: React.FC<{ matters: FirmTopMatter[]; currency: string }> = ({ matters, currency }) => {
  const { t } = useTranslation();
  const cMatter = t('Matter');
  const cRevenue = t('Revenue');
  const cHours = t('Hours');
  const cUtil = t('Utilization');
  const rows = matters.map((m) => ({
    id: m.matter_uuid,
    [cMatter]: m.matter_title || m.matter_uuid,
    [cRevenue]: money(m.currency || currency, m.revenue),
    [cHours]: hours(m.hours, t('h')),
    [cUtil]: toPct(m.utilization),
    _uuid: m.matter_uuid,
  }));
  return (
    <GlassCard icon={<PiBriefcaseLight />} title={t('Top matters')} padding="none">
      {matters.length === 0 ? <EmptyRow label={t('No data yet.')} /> : (
        <PaneTable
          tableId="firm-top-matters"
          columns={[cMatter, cRevenue, cHours, cUtil]}
          data={rows}
          renderCell={(col, value, row) => (col === cMatter ? (
            <Link
              to={`/matters/${row._uuid}?tab=Economics`}
              className="block truncate text-fg-body hover:text-fg-strong"
              onClick={(e) => e.stopPropagation()}
            >
              {value}
            </Link>
          ) : col === cUtil ? <UtilizationBadge pct={value} /> : undefined)}
        />
      )}
    </GlassCard>
  );
};

/** Top clients by revenue. */
export const TopClientsWidget: React.FC<{ clients: FirmTopClient[]; currency: string }> = ({ clients, currency }) => {
  const { t } = useTranslation();
  const cClient = t('Client');
  const cRevenue = t('Revenue');
  const cHours = t('Hours');
  const rows = clients.map((c, i) => ({
    id: `${c.client_id ?? `unassigned-${i}`}-${c.currency || currency}`,
    [cClient]: c.client_name || (c.client_id ? `#${c.client_id}` : t('Unassigned')),
    // Each row in its own currency (PONS-31) - never the dashboard's.
    [cRevenue]: money(c.currency || currency, c.revenue),
    [cHours]: hours(c.hours, t('h')),
    _currency: c.currency || currency,
  }));
  // A currency badge only when the list spans more than one - then it is
  // what tells a client's EUR row from its USD row (PONS-31).
  const mixed = new Set(clients.map((c) => (c.currency || currency).toLowerCase())).size > 1;
  return (
    <GlassCard icon={<PiBuildingsLight />} title={t('Top clients')} padding="none">
      {clients.length === 0 ? <EmptyRow label={t('No data yet.')} /> : (
        <PaneTable
          tableId="firm-top-clients"
          columns={[cClient, cRevenue, cHours]}
          data={rows}
          renderCell={mixed ? (col, value, row) => (col === cClient ? (
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate">{value}</span>
              <Badge color="gray">{String(row._currency).toUpperCase()}</Badge>
            </span>
          ) : undefined) : undefined}
        />
      )}
    </GlassCard>
  );
};

/**
 * Hours/revenue by user. Clicking a row filters the dashboard to that user
 * (#2280). Utilization is the point of this widget rather than a footnote, so
 * it gets its own labelled column.
 */
export const HoursByUserWidget: React.FC<{
  users: FirmUserProductivity[];
  currency: string;
  onUserClick?: (userId: number) => void;
}> = ({ users, currency, onUserClick }) => {
  const { t } = useTranslation();
  const cUser = t('User');
  const cHours = t('Hours');
  const cRevenue = t('Revenue');
  const cUtil = t('Utilization');
  const rows = users.map((u) => {
    // `utilization` is a 0–1 ratio; `billable_pct` is the same idea when
    // the backend sends the split rather than the ratio.
    const util = u.utilization != null
      ? Math.round(u.utilization * 100)
      : u.billable_pct != null
        ? Math.round(u.billable_pct)
        : null;
    return {
      id: String(u.user_id),
      [cUser]: u.user_name || '',
      [cHours]: hours(u.hours, t('h')),
      [cRevenue]: money(currency, u.revenue),
      [cUtil]: util,
      _user: u,
    };
  });
  return (
    <GlassCard icon={<PiUsersLight />} title={t('Productivity by user')} padding="none">
      {users.length === 0 ? <EmptyRow label={t('No data yet.')} /> : (
        <PaneTable
          tableId="firm-hours-by-user"
          columns={[cUser, cHours, cRevenue, cUtil]}
          data={rows}
          onRowClick={onUserClick ? (row) => onUserClick(row._user.user_id) : undefined}
          renderCell={(col, _value, row) => (col === cUser ? (
            <UserCell user={row._user} fallbackLabel={row._user.user_name || undefined} />
          ) : col === cUtil ? <UtilizationBadge pct={row[cUtil]} /> : undefined)}
        />
      )}
    </GlassCard>
  );
};

/**
 * "2026-05" → "May 2026" in the active language; anything else passes through
 * unchanged. Month names come from `Intl`, never a hardcoded English array.
 */
export const formatPeriod = (period: string, locale?: string): string => {
  const m = /^(\d{4})-(\d{2})$/.exec(period);
  if (!m) return period;
  const monthIdx = Number(m[2]) - 1;
  if (monthIdx < 0 || monthIdx > 11) return period;
  try {
    const label = new Intl.DateTimeFormat(locale || undefined, { month: 'short' })
      .format(new Date(Date.UTC(2000, monthIdx, 1)));
    return `${label} ${m[1]}`;
  } catch {
    return period;
  }
};

/**
 * Revenue over time. The BE returns a monthly series (revenue_trend periods like
 * "2026-05"), so this is bucketed by month; bars read better than a line for the
 * typically-sparse early data (one or two points).
 */
export const RevenueTrendWidget: React.FC<{
  trend: Array<{ period: string; revenue: number }>;
  currency: string;
}> = ({ trend, currency }) => {
  const { t, i18n } = useTranslation();
  const tokens = useChartTokens();
  const locale = i18n?.language;

  const data = useMemo(() => ({
    labels: trend.map((p) => formatPeriod(p.period, locale)),
    datasets: [{
      label: `${t('Revenue')} (${currency.toUpperCase()})`,
      data: trend.map((p) => p.revenue),
      backgroundColor: tokens.palette[0],
      hoverBackgroundColor: tokens.champagne,
      borderRadius: 6,
      maxBarThickness: 48,
    }],
  }), [trend, currency, t, tokens, locale]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx: any) => formatCurrency(Number(ctx.parsed.y || 0), currency) } },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { color: tokens.grid },
        ticks: { color: tokens.muted, font: { size: 10 } },
      },
      y: {
        grid: { color: tokens.grid },
        border: { display: false },
        ticks: { color: tokens.muted, font: { size: 10 }, maxTicksLimit: 5 },
      },
    },
  }), [currency, tokens]);

  return (
    <GlassCard icon={<PiChartBarLight />} title={t('Revenue trend')}>
      {/* A short, wide strip: the dashboard lays this pane across the full
          width, so a modest fixed height keeps the bars in proportion. */}
      <div className="h-44">
        {trend.length ? <Bar data={data} options={options} /> : <EmptyRow label={t('No data yet.')} />}
      </div>
    </GlassCard>
  );
};
